/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { useAppDispatch } from "@src/config/store";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { IRootState } from "@src/config/reducers";
import { useSelector } from "react-redux";
import WarningIcon from "@advapacs/common/src/assets/icons/ic-warning.png";
import {
  AppAlert,
  defaultIMobileNumber,
  emailValidator,
  ErrorMessageValidator,
  IMobileNumber,
  LaddaButton,
  MobileNumberSelect,
  OptionTypeBase,
  REGEX_MOBILE_NUMBER,
  SelectChangeFunction,
} from "@advapacs/common";
import axios from "axios";
import { UnauthenticatedActions } from "./ducks/slices";
import { formatRegionUrl } from "@src/utils/utils";
import { FormLabel } from "react-bootstrap";
import useCountryCodePrefixDropdown from "@src/hooks/useCountryCodePrefixDropdown";
import SimpleReactValidator from "simple-react-validator";
import parsePhoneNumberFromString, {
  parsePhoneNumber,
} from "libphonenumber-js";
import { useTranslation } from "react-i18next";

interface ISuspicousActivity {
  submitInfo?: () => void;
  sms?: boolean;
  goBack?: () => void;
}

export const SuspicousActivity: FC<ISuspicousActivity> = ({
  submitInfo,
  sms,
  goBack,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { loading, configuration, errorMessage } = useSelector(
    (state: IRootState) => state.unAuthenticatedReducer
  );
  const [email, setEmail] = useState("");
  const timerCount = useRef(60);
  const timer = useRef<NodeJS.Timeout>(null);
  const [exceedError, setExceedError] = useState(false);
  const countryCodePrefixes = useCountryCodePrefixDropdown();
  const [mobilePhoneModel, setMobilePhoneModel] =
    useState<IMobileNumber>(defaultIMobileNumber);
  const [, setError] = useState({});

  const validator = useMemo(() => {
    return new SimpleReactValidator({
      messages: {
        required: `The :attribute field is required.`,
      },
      validators: {
        validMobileNumber: {
          message: `The mobile phone number is invalid.`,
          rule: ({
            mobilePhone,
            nationalNumber,
          }: {
            mobilePhone: string;
            nationalNumber?: string;
          }) => {
            if (nationalNumber?.length > 0) {
              if (!REGEX_MOBILE_NUMBER.test(mobilePhone)) return false;
              const prefixValue = parsePhoneNumberFromString(mobilePhone);
              if (
                prefixValue &&
                prefixValue?.country &&
                prefixValue?.nationalNumber
              ) {
                return parsePhoneNumber(
                  prefixValue.nationalNumber,
                  prefixValue.country
                ).isValid();
              }
              return false;
            }
          },
          // @ts-ignore
          required: sms,
        },
        ...emailValidator(t),
      },
    });
  }, [t, sms]);

  const submit = async () => {
    try {
      if (!validator?.allValid()) {
        validator.showMessages();
        setError({});
        return;
      }
      axios.defaults.baseURL = formatRegionUrl(
        localStorage.getItem(`${window.location.host}.region`)
      ); // Always overwrite new region detected.
      const data = { channel: sms ? "SMS" : "EMAIL", phone: null, email: null };
      if (sms) {
        data.phone =
          mobilePhoneModel.countryCode.label + mobilePhoneModel.mobilePhone;
      } else {
        data.email = email;
      }
      const rs = await axios.post(
        `/auth/${configuration?.token}/sendWithVerify`,
        data
      );
      dispatch(UnauthenticatedActions.suspicousActivitySuccess(rs?.data));
    } catch (err) {
      dispatch(
        UnauthenticatedActions.loginFail({
          err: err?.response?.data,
          pathname: "/",
        })
      );
    } finally {
      clearInterval(timer?.current);
      timerCount.current = 0;
    }
  };

  useEffect(() => {
    if (errorMessage) {
      switch (errorMessage.code) {
        case "3406":
          if (errorMessage?.type === "PatientPortalException") {
            dispatch(UnauthenticatedActions.suspicousActivityFail());
          }
          setExceedError(false);
          break;
        case "429":
          if (errorMessage?.type === "RateLimitedException") {
            setExceedError(true);
          }
          break;
        default:
          setExceedError(false);
          break;
      }
    }
  }, [errorMessage, dispatch]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMobilePhoneModel({
      ...mobilePhoneModel,
      mobilePhone: event.target.value,
    });
  };

  const onSelectChange: SelectChangeFunction = (
    newValue: OptionTypeBase,
    actionMeta
  ) => {
    const attribute = actionMeta?.name;
    if (attribute) {
      setMobilePhoneModel({
        ...mobilePhoneModel,
        countryCode: newValue,
      });
    }
  };

  return (
    <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <img src={WarningIcon} className="m-r-xxs p-b-lg" />
      <div className="w-100">
        <div className="col-lg-12 col-xs-12">
          <h5 className="m-b-sm">Suspicious Activity Detected</h5>
        </div>

        <div className="col-lg-12">
          <p className="d-inline">
            Our system has detected unusual activity in your account. To ensure
            your security and continue, please enter your contact detail to
            verify.
          </p>
        </div>

        <div className="col-lg-12 m-b-lg">
          <p
            onClick={() => {
              if (submitInfo) submitInfo();
            }}
            className="text-primary font-weight cursor-pointer"
          ></p>
        </div>

        {sms && (
          <div className="col-lg-12 text-left">
            <p className="label m-b-xxs w-100 text-center1">
              Phone
              <span className="text-danger">*</span>
            </p>
            <div>
              <MobileNumberSelect
                readOnly={false}
                mobileNumber={{
                  countryCode: mobilePhoneModel?.countryCode,
                  mobilePhone: mobilePhoneModel?.mobilePhone,
                }}
                countryCode={countryCodePrefixes}
                handleSelectChange={onSelectChange}
                onChange={handleInputChange}
                validator={validator}
                labelName={null}
                mobileNumberInvalidMessage={
                  "The mobile phone number is invalid."
                }
                defaultColValue={12}
                isModal={false}
                isShowLabelCol={false}
              />
            </div>
          </div>
        )}

        {!sms && (
          <div className="col-lg-12 text-left">
            <div className="form-group row">
              <FormLabel className={`col-lg-2 col-form-label`}>
                Email
                <span className="text-danger">*</span>
              </FormLabel>
              <div className={`col-lg-12`}>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  readOnly={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      submit();
                    }
                  }}
                />
                <ErrorMessageValidator
                  errorMessage={validator.message(
                    "Email",
                    email,
                    "email|disposableEmail"
                  )}
                />
              </div>
            </div>
          </div>
        )}

        <div className="col-lg-12 m-t-lg w-100">
          <LaddaButton
            loading={loading}
            type="button"
            className="btn btn-primary btn-verify"
            disabled={
              (sms &&
                (!mobilePhoneModel.mobilePhone ||
                  !mobilePhoneModel.countryCode)) ||
              (!sms && !email)
            }
            btnName="Submit"
            onClickHandler={submit}
          />
        </div>

        <div className="col-lg-12 m-t-md otp-form">
          {exceedError && (
            <AppAlert.SimpleAlert
              variant="warning"
              className="text-left expired-error"
              message="Unable to verify contact detail due to too many attempts. Please try again later."
            />
          )}

          <p
            onClick={() => {
              if (goBack) goBack();
            }}
            className={"text-primary font-weight cursor-pointer"}
          >
            Back
          </p>
        </div>
      </div>
    </form>
  );
};
